import { RoomDescriptorSchema } from '@andor/contracts/rooms';
import { RoomsService as RoomsApi } from '../../shared/api/generated/rooms/rooms.service';
import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { Room, RoomsStore, roomFromDescriptor } from '../../state/rooms.repository';
import { RoomSchedule } from '../../shared/models/schedule';

/**
 * Partial room metadata update. Every field is optional and only the provided
 * ones change server-side; `schedule` is tri-state — omit to keep the current
 * one, `null` to clear it, an object to set it.
 */
export interface RoomUpdate {
  name?: string;
  isPrivate?: boolean;
  hasPassword?: boolean;
  schedule?: RoomSchedule | null;
}

/**
 * Talks to the andor-server room-management endpoints — updating a room's
 * metadata and closing (permanently deleting) a room, both gated behind the
 * `manageRooms` permission server-side.
 *
 * A pure app-server HTTP service in the spirit of `ModerationService`: it owns
 * the `RoomsStore` reconciliation for the operations it performs rather than
 * routing every store write through `AppService`. Auth headers are attached
 * automatically by `defaultHeadersInterceptor`.
 */
@Injectable({ providedIn: 'root' })
export class RoomService {
  private readonly roomsApi = inject(RoomsApi);

  private _roomsStore = inject(RoomsStore);

  /**
   * Close (permanently delete) a room on the server. Resolves to `true` once
   * the room is gone, `false` on a real failure.
   *
   * On success we drop the room from `RoomsStore` immediately for instant
   * feedback — a `200` means the server already committed the delete. The
   * `room-closed` WebSocket broadcast lands shortly after and is an idempotent
   * no-op. A `404 ROOM_NOT_FOUND` means it was already gone, which we also treat
   * as success so the UI clears the stale row.
   */
  /**
   * Update a room's metadata. Resolves to the updated room, or `null` when the
   * update failed (including a `404`, meaning the room is gone — the stale row
   * is dropped from `RoomsStore` in that case).
   *
   * On success we upsert the returned descriptor immediately for instant
   * feedback; the `room-updated` WebSocket broadcast that follows carries the
   * same descriptor and is an idempotent no-op.
   */
  async updateRoom(roomId: string, patch: RoomUpdate): Promise<Room | null> {
    try {
      const descriptor = await firstValueFrom(
        this.roomsApi.updateRoom({
          roomId,
          ...patch,
        })
      );
      const room = roomFromDescriptor(RoomDescriptorSchema.parse(descriptor));
      this._roomsStore.add(room);
      return room;
    } catch (e) {
      if (e instanceof HttpErrorResponse && e.status === 404) {
        this._roomsStore.remove(roomId);
      } else {
        console.error('Failed to update room', e);
      }
      return null;
    }
  }

  async closeRoom(roomId: string): Promise<boolean> {
    try {
      await firstValueFrom(this.roomsApi.closeRoom({ roomId }));
    } catch (e) {
      if (!(e instanceof HttpErrorResponse && e.status === 404)) {
        console.error('Failed to close room', e);
        return false;
      }
    }
    this._roomsStore.remove(roomId);
    return true;
  }
}
